import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category labels
const CATEGORY_LABELS = {
  'bracelets': 'Bracelets',
  'anklets': 'Anklets',
  'tie-dye': 'Tie Dye Apparel',
  'baskets': 'Woven Baskets'
};

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('mrsbum').filter((i) => i.to !== '/shop/wicks') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function MrsBumProduct() {
  const { category, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addItem, itemCount, openCart } = useCart();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const categoryLabel = CATEGORY_LABELS[category] || category;

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrsbum/product/${productId}`);
      setProduct(response.data);
      // Set default size if sizes available
      if (response.data.sizes && response.data.sizes.length > 0) {
        setSelectedSize(response.data.sizes[0]);
      }
    } catch (error) {
      toast.error('Product not found');
      navigate(`/shop/mrsbum/${category}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="substack-site">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const outOfStock = product.stock === 0;
  // A single piece: no quantity picker, "One of a kind" instead of "Only 1
  // left", and "Sold" once it's gone.
  const oneOfAKind = !!product.one_of_a_kind;
  // Missing/null stock means unlimited — keep the existing 1-5 picker in
  // that case; otherwise cap options at whatever's actually left.
  const maxQty = product.stock == null ? 5 : Math.max(1, Math.min(5, product.stock));
  const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1);

  // Gather all images for gallery
  const allImages = [product.image_url, ...(product.additional_images || [])].filter(Boolean);

  const handleAddToCart = () => {
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    addItem({
      shop: 'mrsbum',
      product_id: product.id,
      product_title: product.title,
      image_url: product.image_url,
      variant: selectedSize || null,
      variant_label: selectedSize ? `Size ${selectedSize}` : null,
      price: product.price,
      quantity: quantity,
      max_quantity: product.stock,
      special_instructions: null
    });
    toast.success(`${product.title} added to cart!`);
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Main Content */}
      <main style={{ 
        maxWidth: '1100px', 
        margin: '0 auto', 
        padding: '30px 20px 60px' 
      }}>
        <div 
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '50px',
            alignItems: 'start'
          }}
        >
          {/* Left Column - Image Gallery */}
          <div>
            {/* Main Image */}
            <div style={{
              position: 'relative',
              marginBottom: '15px'
            }}>
              <img 
                src={allImages[selectedImageIndex]}
                alt={product.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '4px'
                }}
              />
              
              {/* Navigation Arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255,255,255,0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnail Row */}
            {allImages.length > 1 && (
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center'
              }}>
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    style={{
                      width: '60px',
                      height: '60px',
                      padding: 0,
                      border: selectedImageIndex === idx ? '2px solid #7c3aed' : '2px solid transparent',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: 'none'
                    }}
                  >
                    <img 
                      src={img} 
                      alt={`${product.title} ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Product Info */}
          <div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '32px',
              fontWeight: '400',
              margin: '0 0 20px',
              color: '#1a1a1a'
            }}>
              {product.title}
            </h1>

            {product.subtitle && (
              <p style={{
                fontSize: '16px',
                color: '#666',
                margin: '0 0 20px',
                fontStyle: 'italic',
                lineHeight: '1.6'
              }}>
                {product.subtitle}
              </p>
            )}

            {product.description && (
              <p style={{
                fontSize: '15px',
                color: '#444',
                lineHeight: '1.7',
                margin: '0 0 25px'
              }}>
                {product.description}
              </p>
            )}

            {/* Features List */}
            {product.features && product.features.length > 0 && (
              <ul style={{
                listStyle: 'disc',
                paddingLeft: '20px',
                margin: '0 0 25px',
                color: '#555'
              }}>
                {product.features.map((feature, idx) => (
                  <li key={idx} style={{ 
                    fontSize: '14px', 
                    lineHeight: '1.8',
                    marginBottom: '4px'
                  }}>
                    {feature}
                  </li>
                ))}
              </ul>
            )}

            {/* Additional description for care/shipping */}
            {product.care_instructions && (
              <p style={{
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.6',
                margin: '0 0 20px'
              }}>
                {product.care_instructions}
              </p>
            )}

            {/* Price */}
            <div style={{
              fontSize: '28px',
              fontWeight: '500',
              color: '#1a1a1a',
              marginBottom: '25px'
            }}>
              ${product.price}
              {oneOfAKind && (
                <span style={{
                  marginLeft: '14px', verticalAlign: 'middle', display: 'inline-block',
                  background: outOfStock ? '#f3f4f6' : '#f3e8ff', color: outOfStock ? '#6b7280' : '#7c3aed',
                  padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600'
                }}>
                  {outOfStock ? 'Sold' : 'One of a kind'}
                </span>
              )}
            </div>

            {/* Size Selector */}
            {product.sizes && product.sizes.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '10px',
                  color: '#333'
                }}>
                  Select Size
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {product.sizes.map(size => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setSelectedSize(size)}
                      style={{
                        padding: '10px 20px',
                        border: selectedSize === size ? '2px solid #7c3aed' : '1px solid #ccc',
                        borderRadius: '4px',
                        background: selectedSize === size ? '#f3e8ff' : '#fff',
                        color: selectedSize === size ? '#7c3aed' : '#333',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.15s'
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                
                {/* Size descriptions */}
                {product.size_descriptions && selectedSize && product.size_descriptions[selectedSize] && (
                  <p style={{
                    fontSize: '13px',
                    color: '#666',
                    marginTop: '8px'
                  }}>
                    {product.size_descriptions[selectedSize]}
                  </p>
                )}
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              {!oneOfAKind && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '14px', color: '#666' }}>Qty:</label>
                  <select
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                    style={{
                      padding: '10px 15px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      background: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    {qtyOptions.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              )}

              {!oneOfAKind && product.stock != null && product.stock > 0 && product.stock <= 5 && (
                <span style={{ fontSize: '13px', color: '#b45309' }}>
                  Only {product.stock} left
                </span>
              )}

              {outOfStock ? (
                <div style={{ padding: '12px 30px', background: '#f3f4f6', color: '#9ca3af', borderRadius: '6px', fontSize: '15px', fontWeight: '500' }}>
                  {oneOfAKind ? 'Sold' : 'Out of Stock'}
                </div>
              ) : (
                <Button
                  onClick={handleAddToCart}
                  style={{
                    padding: '12px 30px',
                    fontSize: '15px',
                    fontWeight: '500',
                    background: '#7c3aed',
                    borderColor: '#7c3aed'
                  }}
                >
                  Add to Cart
                </Button>
              )}
            </div>

            {/* Back link */}
            <Link 
              to={`/shop/mrsbum/${category}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: '#7c3aed',
                textDecoration: 'none',
                fontSize: '14px',
                marginTop: '15px'
              }}
            >
              <ArrowLeft size={16} />
              Back to {categoryLabel}
            </Link>

            {/* Shipping Info */}
            <p style={{
              fontSize: '13px',
              color: '#888',
              margin: '30px 0 0',
              paddingTop: '20px',
              borderTop: '1px solid #eee'
            }}>
              Free shipping on orders over $75 • US shipping only
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          main > div {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default MrsBumProduct;
